import React, { useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import MobileHamburgerMenu from '../components/MobileHamburgerMenu';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';

/**
 * MobileLayout Component
 * Provides a layout structure for mobile views with a hamburger menu
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element}
 */
const MobileLayout = ({ children, currentLecture }) => {
  const { config, theme } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine logo source (similar logic as DesktopLayout/Sidebar)
  const organization = config?.organization || {};
  const logoSrc =
    organization.logo ||
    config.logo_url ||
    config.logo ||
    '/images/default-logo.png'; // fallback

  // Determine center name
  const centerName = organization.name || config.name || 'Center Name'; // fallback

  return (
    <div className="mobile-layout" style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      position: 'relative',
    }}>
      <header style={{
        backgroundColor: theme.colors.primary,
        color: 'white',
        padding: '12px 16px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Link to="/" style={{ 
            textDecoration: 'none', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {/* Logo — white bg ensures transparent logos stay visible on dark headers */}
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              boxShadow: theme.boxShadow.small,
              flexShrink: 0
            }}>
              <img
                src={logoSrc} // Use dynamic logo source
                alt={centerName} // Use dynamic alt text
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <h1 style={{ margin: 0, fontSize: '2rem', whiteSpace: 'nowrap' }}>{centerName}</h1> {/* Use dynamic center name */}
          </Link>
          
          {/* Hamburger menu button is now part of the MobileHamburgerMenu component */}
        </div>
      </header>

      {/* Add the new MobileHamburgerMenu component */}
      <MobileHamburgerMenu currentLecture={currentLecture} />

      <main
        className={theme.isDarkBackground ? 'dark-content-bg' : ''}
        style={{
          flex: 1,
          width: '100%',
          paddingBottom: '64px',
          backgroundColor: theme.colors.background,
          '--heading-color': theme.colors.headingColor,
          '--subtitle-color': theme.colors.subtitleColor,
          '--link-color': theme.colors.linkColor,
          '--text-color': theme.colors.text,
          '--text-secondary': theme.colors.cardTextSecondary || '#666666',
          '--card-bg': theme.colors.cardBg,
          '--page-bg': theme.colors.background,
          '--border-color': theme.isDarkBackground ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
          '--scrollbar-thumb': `${theme.colors.primary}40`,
          '--scrollbar-thumb-hover': `${theme.colors.primary}70`,
        }}
      >
        {children}
      </main>

      <footer style={{
        backgroundColor: theme.colors.primary,
        color: 'white',
        padding: '24px 16px',
        marginTop: 'auto',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Link to="/" style={{ 
            textDecoration: 'none', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            {/* Logo in footer */}
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              flexShrink: 0
            }}>
              <img
                src={logoSrc} // Use dynamic logo source
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>{centerName}</span> {/* Use dynamic center name */}
          </Link>
          <p style={{ margin: '4px 0', fontSize: '12px', opacity: 0.8, textAlign: 'center' }}>
            {config.message || 'Providing lectures and educational content'} {/* Use dynamic message or default */}
          </p>
          <p style={{ margin: '4px 0', fontSize: '12px', textAlign: 'center' }}>
            &copy; {new Date().getFullYear()} {centerName} - All rights reserved {/* Dynamic copyright */}
          </p>
          <Link
            to={`/feedback${location.search}`}
            style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
              textDecoration: 'underline',
              marginTop: '4px'
            }}
          >
            Share Feedback
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default MobileLayout;
